import {
  Box,
  Button,
  CircularProgress,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "../../utils/hooks";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { resetResponseFormState } from "../../Redux/reducers/responseSlice";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import { HeaderRightContent } from "../Common/styles/header";
import { computeEditablePermission } from "../../Redux/actions/responseAction";
import { useState } from "react";
import CreateResponseModal from "./CreateResponseModal";
import HeaderTags from "./HeaderTags";
import BackButton from "../Common/UI/BackButton";
import { toastMessage } from "../../utils/toast";
import {
  canEditScreeningByEligibility,
} from "../../utils/screeningEditEligibility";
import { fetchIncompleteScreeningSummary } from "../../utils/screeningIncompleteSummary";
import type { IncompleteScreeningForm } from "../../utils/screeningEditEligibility";
import IncompleteScreeningConfirmModal from "./Screening/IncompleteScreeningConfirmModal";
import FinishScreeningModal from "../PlatformParticipant/FinishScreeningModal";
import FinishSurveyModal from "./Survey/FinishSurveyModal";

export const responsesHeader: SxProps = {
  bgcolor: "background.paper",
  py: 3,
  px: 2,
  height: "72px",
};

type Props = {
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeStatus: React.Dispatch<React.SetStateAction<boolean>>;
};

const FormHeader = ({
  setShowModal,
  setChangeStatus,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id, participantId } = useParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showSaveCompleteModal, setShowSaveCompleteModal] = useState(false);
  const [showFinishSurveyModal, setShowFinishSurveyModal] = useState(false);
  const [pendingFinishAction, setPendingFinishAction] = useState<
    "finishReview" | "saveComplete" | null
  >(null);
  const [incompleteForms, setIncompleteForms] = useState<
    IncompleteScreeningForm[]
  >([]);
  const participantLabel = useQuery().get("participant");

  const currentCategory = useAppSelector(
    (state) => state.response.currentCategory
  );

  const canSendSurveys = useAppSelector(
    (state) => state.response.canSendSurveys
  );
  const email = useAppSelector((state) => state.response.email);
  const isResponseSubmitting = useAppSelector(
    (state) => state.response.fieldSubmitting
  );
  const isResponseLoading = useAppSelector((state) => state.response.isLoading);
  const attemptId = useAppSelector((state) => state.response.repeatedAttemptId);
  const surveyId = useAppSelector((state) => state.response.surveyAssignmentId);
  const surveyAssignmentStatus = useAppSelector(
    (state) => state.response.surveyAssignmentStatus
  );
  const phaseList = useAppSelector((state) => state.response.phaseList);
  const canEditSurveys = useAppSelector(
    (state) => state.response.canEditSurveys
  );
  const canEditScreening = useAppSelector(
    (state) => state.response.canEditScreening
  );
  const eligibilityStatus = useAppSelector(
    (state) => state.response.eligibilityStatus
  );
  const screeningBtn = useAppSelector((state) => state.response.screeningBtn);
  const selectedForm = useAppSelector((state) => state.response.selectedForm);
  const screeningAttemptId = useAppSelector(
    (state) => state.response.screeningAttemptId
  );
  const isRegistryParticipant = useAppSelector(
    (state) => state.response.isRegistryParticipant
  );
  const isEditable = useAppSelector((state) => state.response.editable);

  const isIncluded = eligibilityStatus === "included";
  const canEditByEligibility = canEditScreeningByEligibility(eligibilityStatus);
  const showFillScreener =
    !screeningAttemptId && canEditScreening && canEditByEligibility;
  const showEditResponses =
    !!screeningAttemptId &&
    !isEditable &&
    canEditScreening &&
    canEditByEligibility;
  const showFinishCta =
    !!screeningAttemptId && canEditByEligibility && isEditable;
  const allSurveyFormsCompleted =
    phaseList.length > 0 &&
    phaseList.every((phase) =>
      (phase.phaseForms || []).every((form) => form.status === "completed")
    );
  const showSurveyFinishCta =
    currentCategory === "surveys" &&
    !!surveyId &&
    isEditable &&
    canEditSurveys &&
    surveyAssignmentStatus !== "completed" &&
    allSurveyFormsCompleted;

  const clearRepeatingId = () => {
    if (isResponseSubmitting || isResponseLoading) return;

    dispatch(resetResponseFormState());
    dispatch(computeEditablePermission(currentCategory));
  };

  const handleBack = () => {
    if (isResponseSubmitting || isResponseLoading) return;

    navigate(-1);
  };

  const handleNavigate = () => {
    if (isResponseSubmitting || isResponseLoading) return;

    navigate(
      `/studies/${id}/participants/${participantId}/details?participant=${participantLabel}`
    );
  };

  const navigateToCompare = () => {
    if (selectedForm)
      navigate(
        `/studies/${id}/participants/${participantId}/responses/${selectedForm?.id}/compare?formName=${selectedForm?.name}`
      );
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const proceedAfterIncompleteCheck = (
    action: "finishReview" | "saveComplete"
  ) => {
    if (action === "saveComplete") {
      setShowSaveCompleteModal(true);
    } else {
      setChangeStatus(false);
      setShowModal(true);
    }
  };

  const startFinishFlow = async (action: "finishReview" | "saveComplete") => {
    if (!participantId || !screeningAttemptId) {
      proceedAfterIncompleteCheck(action);
      return;
    }
    try {
      const incomplete = await fetchIncompleteScreeningSummary(
        participantId,
        screeningAttemptId,
        { studyId: id }
      );
      if (incomplete.length > 0) {
        setIncompleteForms(incomplete);
        setPendingFinishAction(action);
        setShowIncompleteModal(true);
        return;
      }
    } catch {
      // Fall through to finish flow if preview fails
    }
    proceedAfterIncompleteCheck(action);
  };

  const finishReview = () => {
    void startFinishFlow("finishReview");
  };

  const saveAndComplete = () => {
    void startFinishFlow("saveComplete");
  };

  const handleIncompleteContinue = () => {
    setShowIncompleteModal(false);
    if (pendingFinishAction) {
      proceedAfterIncompleteCheck(pendingFinishAction);
    }
    setPendingFinishAction(null);
  };

  const handlePrint = () => {
    if (!selectedForm || !id || !participantId) {
      toastMessage("warning", "Please select a form to print");
      return;
    }

    const params = new URLSearchParams({
      studyId: id,
      formId: selectedForm.id,
      formName: selectedForm.name || "",
      participantId: participantId,
      category: currentCategory || "visit",
    });

    if (attemptId) {
      params.append("repeatedAttemptId", attemptId);
    }

    if (screeningAttemptId) {
      params.append("screeningAttemptId", screeningAttemptId);
    }

    if (isRegistryParticipant) {
      params.append("isRegistryParticipant", "true");
    }

    const printUrl = `/print/form-response?${params.toString()}`;
    window.open(printUrl, "_blank");
  };

  return (
    <Stack
      sx={responsesHeader}
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={2}
    >
      <Stack
        direction={"row"}
        gap={1}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <BackButton onClick={handleBack} />
        <Box>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography fontWeight={"medium"} variant="subtitle1">
              Participant ID: {participantLabel ?? participantId}
            </Typography>
            <Box>
              {email && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="regular"
                >
                  {email}
                </Typography>
              )}
            </Box>
          </Box>
          <HeaderTags />
        </Box>
      </Stack>

      <Box sx={HeaderRightContent}>
        {isResponseSubmitting && <CircularProgress color="primary" size={24} />}
        {showSurveyFinishCta && (
          <Button
            variant="contained"
            onClick={() => setShowFinishSurveyModal(true)}
          >
            Complete Survey
          </Button>
        )}
        {currentCategory === "screening" && (
          <>
            {screeningBtn === "compare" && (
              <Button variant="outlined" onClick={() => navigateToCompare()}>
                Compare responses
              </Button>
            )}
            {(showFillScreener || showEditResponses) && (
              <Button variant="outlined" onClick={() => openCreateModal()}>
                {showFillScreener ? "Fill Screener" : "Edit responses"}
              </Button>
            )}
          </>
        )}
        {currentCategory === "screening" && canEditScreening && (
          <>
            {showFinishCta && (
              <Button
                variant="contained"
                onClick={isIncluded ? saveAndComplete : finishReview}
              >
                {isIncluded ? "Save and Complete" : "Finish Review"}
              </Button>
            )}
            {eligibilityStatus === "decide_later" && !isEditable && (
              <Button
                variant="contained"
                onClick={() => {
                  setChangeStatus(true);
                  setShowModal(true);
                }}
              >
                Change Status
              </Button>
            )}
          </>
        )}
        {selectedForm && (
          <Button
            variant="outlined"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
        )}
        <Button variant="contained" onClick={handleNavigate}>
          View Details
        </Button>
        {attemptId && (
          <Button variant="contained" onClick={clearRepeatingId}>
            View All Repeating Data
          </Button>
        )}
        {currentCategory === "surveys" &&
          canSendSurveys &&
          isIncluded &&
          !surveyId && (
          <Button
            variant="contained"
            onClick={() => {
              setChangeStatus(false);
              setShowModal(true);
            }}
            startIcon={<AddIcon />}
          >
            New Survey Invitation
          </Button>
        )}
        {surveyId && (
          <Button variant="contained" onClick={clearRepeatingId}>
            View all Surveys
          </Button>
        )}
      </Box>
      {showFinishSurveyModal && (
        <FinishSurveyModal
          showModal={showFinishSurveyModal}
          closeModal={() => setShowFinishSurveyModal(false)}
        />
      )}
      {showCreateModal && (
        <CreateResponseModal
          showModal={showCreateModal}
          closeModal={closeCreateModal}
        />
      )}
      {showIncompleteModal && (
        <IncompleteScreeningConfirmModal
          open={showIncompleteModal}
          onClose={() => {
            setShowIncompleteModal(false);
            setPendingFinishAction(null);
          }}
          onContinue={handleIncompleteContinue}
          incompleteForms={incompleteForms}
        />
      )}
      {showSaveCompleteModal && (
        <FinishScreeningModal
          showModal={showSaveCompleteModal}
          closeModal={() => setShowSaveCompleteModal(false)}
          variant="study"
          skipIncompleteCheck
        />
      )}
    </Stack>
  );
};
export default FormHeader;
